// محرّك المزامنة: يدفع الطابور ثم يسحب التغييرات.
//
// قواعد تحكم هذا الملف:
//  • الدفع قبل السحب دائماً — وإلا سحبنا نسخة قديمة فوق تعديل لم يصل بعد.
//  • مزامنة واحدة في كل لحظة؛ الطلبات المتزامنة تنتظر الجارية بدل أن تتسابق.
//  • تراجع أُسّي عند الفشل: شبكة متقطّعة لا يجوز أن تتحوّل إلى قصف للخادم.
//  • الفشل ليس خطأً مرئياً — التطبيق يعمل بلا اتصال بالتصميم، والطابور يصبر.

import { api, ApiError, isOnline, onConnectivity } from './api.js';
import { state, mergeFromServer, clearQueued, save, setStats, setWindow } from './store.js';

const PUSH_CHUNK = 200;      // حدّ الخادم ٥٠٠؛ نبقى دونه بهامش
const MAX_BACKOFF = 5 * 60 * 1000;

let syncing = false;
let pending = null;          // وعد المزامنة الجارية
let failures = 0;
let timer = null;
const listeners = new Set();

export const SyncStatus = {
  IDLE: 'idle', SYNCING: 'syncing', OFFLINE: 'offline', ERROR: 'error', AUTH: 'auth'
};
let status = SyncStatus.IDLE;

export function onSyncChange(fn){
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(next){
  status = next;
  listeners.forEach(fn => { try { fn(status, state.queue.length); } catch (err){ console.warn(err); } });
}

export function syncStatus(){ return status; }

/**
 * يزامن الآن. آمن للاستدعاء المتكرر.
 * @param {{force?:boolean}} opts force يتجاهل التراجع الأُسّي (ضغط المستخدم زر المزامنة)
 */
export function sync(opts = {}){
  if (pending) return pending;
  pending = runSync(opts).finally(() => { pending = null; });
  return pending;
}

async function runSync({ force = false } = {}){
  if (!state.user) return { skipped: 'no-user' };

  if (!isOnline() && !force){
    emit(SyncStatus.OFFLINE);
    return { skipped: 'offline' };
  }

  syncing = true;
  emit(SyncStatus.SYNCING);

  try {
    const pushed = await pushQueue();
    const pulled = await pullChanges();
    await refreshStats();

    failures = 0;
    clearTimeout(timer);
    emit(SyncStatus.IDLE);
    return { pushed, pulled };

  } catch (err){
    failures++;

    if (err instanceof ApiError && err.isAuth){
      // انتهت الجلسة: لا فائدة من إعادة المحاولة، الطابور يبقى محفوظاً
      emit(SyncStatus.AUTH);
      throw err;
    }
    if (err instanceof ApiError && err.isOffline){
      emit(SyncStatus.OFFLINE);
      scheduleRetry();
      return { skipped: 'offline' };
    }

    console.warn('فشلت المزامنة:', err.message);
    emit(SyncStatus.ERROR);
    scheduleRetry();
    return { error: err.message };

  } finally {
    syncing = false;
  }
}

function scheduleRetry(){
  clearTimeout(timer);
  // 2s, 4s, 8s… بحدّ أقصى ٥ دقائق
  const delay = Math.min(MAX_BACKOFF, 2000 * Math.pow(2, Math.min(failures, 8)));
  timer = setTimeout(() => sync(), delay);
}

/**
 * العدّادات التي يبني عليها العميل ترتيب البنود ومجموع الشهر.
 * تُجلب بعد كل مزامنة ناجحة: أرخص بكثير من سحب الحركات، وتُبقي الشاشة
 * صادقة بعد أن يسجّل فرد آخر من العائلة حركة.
 */
async function refreshStats(){
  const n = new Date();
  const from = new Date(n.getFullYear(), n.getMonth(), 1);
  const to = new Date(n.getFullYear(), n.getMonth() + 1, 0, 23, 59, 59, 999);
  try {
    setStats(await api.getStats(from, to));
  } catch (err){
    // فشل العدّادات لا يُفشل المزامنة: الطابور وصل، وهو الأهم
    console.warn('تعذّر تحديث العدّادات:', err.message);
  }
}

/**
 * يجلب حركات فترة للعرض ثم تُنسى — لا تُخزَّن على الجهاز.
 * هذا ما حلّ محلّ الاحتفاظ بتاريخ المحفظة كاملاً محلياً.
 */
export async function loadRange(from, to, { untyped = false } = {}){
  const payload = await api.listEntries(from, to, { untyped });
  setWindow(from, to, payload.entries, payload.hasMore, untyped);
  return payload;
}

/* ------------------------------------------------------------------ الدفع */

async function pushQueue(){
  let total = 0;

  // نعمل على نسخة: قد يضيف المستخدم عمليات أثناء انتظار الشبكة،
  // ومسح الطابور كاملاً بعد النجاح سيبتلع ما أُضيف في هذه الأثناء.
  while (true){
    const batch = state.queue.slice(0, PUSH_CHUNK);
    if (!batch.length) break;

    const res = await api.push(batch.map(stripOpId));
    const okIds = batch.map(op => op.opId);
    clearQueued(okIds);
    total += batch.length;

    if (res.rejected && res.rejected.length){
      // عملية رفضها الخادم لن تنجح بإعادة المحاولة أبداً — أُسقطت من الطابور
      // بالفعل أعلاه، ونسجّلها لأنها تعني خللاً يستحق التحقيق.
      console.error('عمليات رفضها الخادم:', res.rejected);
    }
    if (batch.length < PUSH_CHUNK) break;
  }
  return total;
}

// opId للمطابقة المحلية فقط ولا معنى له على الخادم
function stripOpId(op){
  const { opId, ...rest } = op;
  return { opId, ...rest };
}

/* ------------------------------------------------------------------ السحب */

async function pullChanges(){
  let count = 0;
  let since = state.lastSync;

  // hasMore: أول مزامنة لعائلة كبيرة قد تتجاوز حدّ الصفحة
  for (let page = 0; page < 50; page++){
    const payload = await api.pull(since);
    mergeFromServer(payload);
    count += (payload.entries?.length || 0) + (payload.dests?.length || 0) + (payload.types?.length || 0);
    if (!payload.hasMore) break;
    since = payload.since;
  }
  return count;
}

/* ------------------------------------------------------- دورة حياة المزامنة */

let started = false;

export function startAutoSync(){
  if (started) return;
  started = true;

  // عودة الاتصال: أهم لحظة للمزامنة على الإطلاق
  onConnectivity(up => { if (up) sync({ force: true }); });

  // العودة إلى التطبيق: الزوج الآخر قد يكون سجّل حركات
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sync();
  });

  // نبضة دورية بينما التطبيق مفتوح
  const heartbeat = setInterval(() => {
    if (document.visibilityState === 'visible') sync();
  }, 60_000);
  heartbeat.unref?.();

  // آخر فرصة لحفظ الطابور قبل إغلاق الصفحة
  window.addEventListener('pagehide', () => {
    save();
    if (state.queue.length && navigator.sendBeacon){
      // sendBeacon يصمد بعد إغلاق الصفحة، بعكس fetch الذي يُلغى
      try {
        navigator.sendBeacon('/api/sync', new Blob(
          [JSON.stringify({ operations: state.queue.slice(0, PUSH_CHUNK).map(stripOpId) })],
          { type: 'application/json' }
        ));
      } catch { /* محاولة أخيرة لا أكثر */ }
    }
  });
}

export function stopAutoSync(){
  clearTimeout(timer);
  failures = 0;
  status = SyncStatus.IDLE;
}
